import { apiClient } from './client';

export interface County {
  id: number;
  name: string;
  code: string;
  maker_checker_enabled?: boolean;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo?: string;
  settings?: CountySettings;
  created_at: string;
  updated_at: string;
}

export interface CountySettings {
  headquarters?: string;
  tagline?: string;
  theme_color?: string;
  website?: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
}

export interface CountySignatory {
  id: number;
  county_id: number;
  name: string;
  title: string;
  signature?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Subcounty {
  id: number;
  county_id: number;
  name: string;
  created_at: string;
  updated_at: string;
  wards_count?: number;
}

export interface Ward {
  id: number;
  subcounty_id: number;
  name: string;
  created_at: string;
  updated_at: string;
  zones_count?: number;
  subcounty?: Subcounty;
}

export interface Zone {
  id: number;
  ward_id: number;
  name: string;
  created_at: string;
  updated_at: string;
  ward?: Ward;
}

export interface CountyUser {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Faq {
  id: number;
  question: string;
  answer: string;
  created_at?: string;
  updated_at?: string;
}

export interface ContactType {
  id: number;
  name: string;
  logo?: string;
}

export interface CountyContact {
  id: number;
  contact_id: number;
  value: string;
  contact?: ContactType;
}

export const countyProfileApi = {
  // Get county profile (includes signatories, name, logo)
  getProfile: async () => {
    const response = await apiClient.get<{ status: string; data: County & { signatories?: CountySignatory[] } }>(
      '/admin/county/profile'
    );
    const data = response.data?.data || response.data;
    if (data && typeof data === 'object' && 'data' in data) {
      return (data as any).data as County & { signatories?: CountySignatory[] };
    }
    return data as County & { signatories?: CountySignatory[] };
  },

  // Update county profile
  updateProfile: async (data: Partial<County>) => {
    const response = await apiClient.put<{ status: string; data: County }>(
      '/admin/county/profile',
      data
    );
    const result = response.data?.data || response.data;
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as any).data as County;
    }
    return result as County;
  },

  // Update logo
  updateLogo: async (logo: string) => {
    const response = await apiClient.post<{ status: string; data: County }>(
      '/admin/county/profile/logo',
      { logo }
    );
    const data = response.data?.data || response.data;
    if (data && typeof data === 'object' && 'data' in data) {
      return (data as any).data as County;
    }
    return data as County;
  },

  // Get signatories
  getSignatories: async () => {
    const response = await apiClient.get<{ status: string; data: CountySignatory[] }>(
      '/admin/county/profile/signatories'
    );
    return response.data?.data || response.data;
  },

  // Add signatory
  addSignatory: async (data: Omit<CountySignatory, 'id' | 'county_id' | 'created_at' | 'updated_at'>) => {
    const response = await apiClient.post<{ status: string; data: CountySignatory }>(
      '/admin/county/profile/signatories',
      data
    );
    const result = response.data?.data || response.data;
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as any).data as CountySignatory;
    }
    return result as CountySignatory;
  },

  // Update signatory
  updateSignatory: async (id: number, data: Partial<CountySignatory>) => {
    const response = await apiClient.put<{ status: string; data: CountySignatory }>(
      `/admin/county/profile/signatories/${id}`,
      data
    );
    const result = response.data?.data || response.data;
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as any).data as CountySignatory;
    }
    return result as CountySignatory;
  },

  // Delete signatory
  deleteSignatory: async (id: number) => {
    await apiClient.delete(`/admin/county/profile/signatories/${id}`);
  },

  // Upload signatory signature
  uploadSignature: async (id: number, signature: string) => {
    const response = await apiClient.post<{ status: string; data: CountySignatory }>(
      `/admin/county/profile/signatories/${id}/signature`,
      { signature }
    );
    const result = response.data?.data || response.data;
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as any).data as CountySignatory;
    }
    return result as CountySignatory;
  },

  // Get activity logs
  getActivityLogs: async (page = 1, perPage = 20, search?: string) => {
    const params = new URLSearchParams({ page: page.toString(), per_page: perPage.toString() });
    if (search) params.append('search', search);
    const response = await apiClient.get<any>(
      `/admin/county/profile/activity-logs?${params.toString()}`
    );
    return response.data?.data || response.data;
  },

  // Get settings
  getSettings: async () => {
    const response = await apiClient.get<{ status: string; data: any }>(
      '/admin/county/profile/settings'
    );
    return response.data?.data || response.data;
  },

  // Update settings
  updateSettings: async (settings: CountySettings) => {
    const response = await apiClient.put<{ status: string; data: any }>(
      '/admin/county/profile/settings',
      { settings }
    );
    return response.data?.data || response.data;
  },

  // Get subcounties
  getSubcounties: async () => {
    const response = await apiClient.get<{ status: string; data: Subcounty[] }>(
      '/admin/county/subcounties'
    );
    return response.data?.data || response.data;
  },

  // Get wards
  getWards: async () => {
    const response = await apiClient.get<{ status: string; data: Ward[] }>(
      '/admin/county/wards'
    );
    return response.data?.data || response.data;
  },

  // Get zones
  getZones: async () => {
    const response = await apiClient.get<{ status: string; data: Zone[] }>(
      '/admin/county/zones'
    );
    return response.data?.data || response.data;
  },

  // Get users
  getUsers: async () => {
    const response = await apiClient.get<{ status: string; data: CountyUser[] }>(
      '/admin/county/users'
    );
    return response.data?.data || response.data;
  },

  // Toggle maker-checker setting
  toggleMakerChecker: async (enabled: boolean) => {
    const response = await apiClient.put<{ status: string; data: County }>(
      '/admin/county/profile/maker-checker',
      { maker_checker_enabled: enabled }
    );
    const result = response.data?.data || response.data;
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as any).data as County;
    }
    return result as County;
  },

  // Get wallet payment activation status for the county
  getWalletStatus: async (): Promise<boolean> => {
    // apiClient.get returns the envelope ({ status, message, data }), so the
    // flag lives at envelope.data.wallet_enabled — not envelope.data.data.
    const envelope = await apiClient.get<{ wallet_enabled: boolean }>(
      '/admin/county/profile/wallet'
    );
    return envelope.data?.wallet_enabled ?? false;
  },

  // Activate / deactivate wallet payments for the county
  toggleWallet: async (enabled: boolean): Promise<boolean> => {
    const envelope = await apiClient.put<{ wallet_enabled: boolean }>(
      '/admin/county/profile/wallet',
      { wallet_enabled: enabled }
    );
    return envelope.data?.wallet_enabled ?? enabled;
  },

  // ==================== FAQs (county-scoped) ====================
  getFaqs: async (): Promise<Faq[]> => {
    const envelope = await apiClient.get<Faq[]>('/admin/county/faqs');
    return envelope.data ?? [];
  },

  createFaq: async (data: { question: string; answer: string }): Promise<Faq> => {
    const envelope = await apiClient.post<Faq>('/admin/county/faqs', data);
    return envelope.data as Faq;
  },

  updateFaq: async (id: number, data: { question: string; answer: string }): Promise<Faq> => {
    const envelope = await apiClient.put<Faq>(`/admin/county/faqs/${id}`, data);
    return envelope.data as Faq;
  },

  deleteFaq: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/county/faqs/${id}`);
  },

  // ==================== Contacts (county-scoped, under /profile) ====================
  getContacts: async (): Promise<CountyContact[]> => {
    const envelope = await apiClient.get<CountyContact[]>('/admin/county/profile/contacts');
    return envelope.data ?? [];
  },

  getContactTypes: async (): Promise<ContactType[]> => {
    const envelope = await apiClient.get<ContactType[]>('/admin/county/profile/contact-types');
    return envelope.data ?? [];
  },

  addContact: async (data: { contact_id: number; value: string }): Promise<CountyContact> => {
    const envelope = await apiClient.post<CountyContact>('/admin/county/profile/contacts/add', data);
    return envelope.data as CountyContact;
  },

  updateContact: async (id: number, data: { contact_id?: number; value?: string }): Promise<CountyContact> => {
    const envelope = await apiClient.put<CountyContact>(`/admin/county/profile/contacts/${id}`, data);
    return envelope.data as CountyContact;
  },

  deleteContact: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/county/profile/contacts/${id}`);
  },
};
